/**
 * Format a date to display time in 12-hour format (e.g., "9:30 AM")
 * Uses IANA timezone when available for accurate location-based formatting.
 *
 * @param {Date} dateObj - The date object to format
 * @param {Object} coordinates - Optional coordinates (unused when timezone is provided)
 * @param {string} timezone - Optional IANA timezone string (e.g., "Australia/Sydney")
 * @returns {string} Formatted time string or "N/A" if invalid
 */
export const safeFormat = (dateObj, coordinates = null, timezone = null) => {
  if (!dateObj) return 'N/A';
  try {
    const options = { hour: '2-digit', minute: '2-digit', hour12: true };

    // Use IANA timezone if available — this is accurate and DST-aware
    if (timezone) {
      options.timeZone = timezone;
      return dateObj.toLocaleTimeString([], options);
    }

    // Fallback: just format using the browser's local timezone
    return dateObj.toLocaleTimeString([], options);
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'N/A';
  }
};

/**
 * Format a date to display time in 12-hour format (e.g., "09:30 AM")
 * @param {Date} date - The date object to format
 * @param {Object} coordinates - Optional coordinates (unused when timezone is provided)
 * @param {string} timezone - Optional IANA timezone string
 * @returns {string} Formatted time string or empty string if invalid
 */
export const formatTime = (date, coordinates = null, timezone = null) => {
  if (!date) return '';
  try {
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };

    if (timezone) {
      options.timeZone = timezone;
    }

    return date.toLocaleTimeString([], options);
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
};

/**
 * Format time until target time in hours, minutes, seconds format
 * @param {Date} targetTime - The target time to calculate difference to
 * @param {Date} now - The current time to use for calculation (optional, defaults to current time)
 * @returns {string} Formatted time string (e.g., "2h 30m 15s")
 */
export const formatTimeUntil = (targetTime, now = new Date()) => {
  if (!targetTime) return '0h 0m 0s';

  // Calculate time difference
  let diff = targetTime - now;

  // Handle potential negative time differences or far future times
  if (diff <= 0) return '0h 0m 0s';

  // If difference is more than 24 hours, it's likely tomorrow and needs adjustment
  if (diff > 24 * 60 * 60 * 1000) {
    const adjustedTarget = new Date(targetTime);
    adjustedTarget.setDate(now.getDate());
    diff = adjustedTarget - now;
    if (diff <= 0) {
      return '0h 0m 0s';
    }
  }

  // Convert to hours, minutes, seconds
  const hours = Math.floor(diff / (1000 * 60 * 60));
  diff -= hours * 1000 * 60 * 60;

  const minutes = Math.floor(diff / (1000 * 60));
  diff -= minutes * 1000 * 60;

  const seconds = Math.floor(diff / 1000);

  return `${hours}h ${minutes}m ${seconds}s`;
};

/**
 * Format duration in seconds to a human-readable string
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration string (e.g., "2h 30m")
 */
export const formatDuration = (seconds) => {
  if (!seconds || isNaN(seconds) || seconds < 0) return 'N/A';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return `${hours}h ${minutes}m`;
};

/**
 * Convert time string to 12-hour format with AM/PM
 * Handles both 24-hour and 12-hour input formats.
 * Also handles timezone suffixes like "05:40 (AEDT)".
 *
 * @param {string} timeStr - Time string in either format (e.g., "13:45" or "1:45 PM")
 * @returns {string} Time in 12-hour format (e.g., "1:45 PM") or original if parsing fails
 */
export const formatTo12Hour = (timeStr) => {
  if (!timeStr) return 'N/A';

  try {
    // If already in 12-hour format (contains AM/PM), return as is
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      return timeStr;
    }

    // Strip timezone suffix like " (AEDT)" if present
    const cleaned = timeStr.replace(/\s*\(.*\)\s*$/, '');

    // Convert from 24-hour format to 12-hour format
    const [hours, minutes] = cleaned.split(':').map(Number);

    if (isNaN(hours) || isNaN(minutes)) {
      return timeStr; // Return original if parsing fails
    }

    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM

    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    console.error('Error formatting time:', error);
    return timeStr; // Return original on error
  }
};
